import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker, Polyline, Popup } from 'react-leaflet';
import { createModeIcon, statusToIconState, STATUS_COLORS } from './modeIcons';
import { modeRowKey } from '../../../utils/modeRows';
import { buildAirRoute } from '../../../utils/airRoute';


/**
 * Air and truck layers for the Global Produce Map.
 *
 * Kept separate from the sea rendering path on purpose. Sea markers are driven
 * by ShipsGo positions, voyage stops and a card-merge pipeline; air and truck
 * rows have none of that — one row is one card, and the position is computed
 * from the schedule. Sharing the sea components would mean teaching them to
 * ignore most of what they do.
 */

const num = (v) => (v == null || v === '' ? NaN : Number(v));
const latLng = (lat, lng) => {
  const a = num(lat);
  const b = num(lng);
  return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
};

export function modeRowPosition(row) {
  return latLng(row?.latitude, row?.longitude);
}

const fmtWeight = (kg) => {
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${Math.round(n).toLocaleString()} kg`;
};

const fmtDate = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime())
    ? String(d)
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Mirrors the sea card's field order so the three modes read the same. */
function ModePopup({ row, mode }) {
  const { t } = useTranslation();
  const isAir = mode === 'air';
  const ref = isAir ? row.awb : row.truck_id;
  const carrier = isAir ? row.airlines : null;
  const arrived = String(row.status || '').toLowerCase() === 'arrived';
  return (
    <div className="mode-popup" style={{ minWidth: 220 }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>
        {row.commodities || (isAir ? 'Air cargo' : 'Truck cargo')}
      </div>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
        {isAir ? 'Flight' : 'Truck'} · {arrived ? 'Arrived' : 'In transit'}
        {row.estimated ? ' · estimated' : ''}
      </div>
      {isAir ? (
        <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
          {t('map.air.estimatedRoute', 'Estimated route · schedule-based position')}
        </div>
      ) : null}
      <Row label="From" value={row.departure_port} />
      <Row label="To" value={[row.arrival_port, row.destination_country].filter(Boolean).join(', ')} />
      {carrier ? <Row label="Airline" value={carrier} /> : null}
      {isAir && ref ? <Row label="AWB" value={ref} /> : null}
      <Row label="Departed" value={fmtDate(row.etd)} />
      <Row label="Arrival" value={fmtDate(row.eta)} />
      <Row label="Weight" value={fmtWeight(row.total_weight)} />
      <Row label="Varieties" value={row.varieties} />
      <Row label="Exporter" value={row.exporters} />
      <Row label="Importer" value={row.importers} />
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, lineHeight: '18px' }}>
      <span style={{ opacity: 0.65 }}>{label}</span>
      <span style={{ textAlign: 'right', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

/**
 * View rows -> render items. Air position, heading and route all come from one
 * path (buildAirRoute); rows without usable endpoints keep their view position.
 * Truck is a straight origin -> destination line.
 */
export function buildModeItems(rows, mode) {
  return (rows || []).map((row) => {
    const key = modeRowKey(row);
    const origin = latLng(row?.origin_lat, row?.origin_lng);
    const dest = latLng(row?.dest_lat, row?.dest_lng);
    const state = statusToIconState(row);
    let pos = modeRowPosition(row);
    let heading = Number(row?.heading) || 0;
    let segments = [];
    let traveledSegments = [];
    let remainingSegments = [];
    if (mode === 'air') {
      const r = buildAirRoute(row);
      if (r.position) {
        pos = r.position;
        heading = r.heading;
        segments = r.segments;
        traveledSegments = r.traveledSegments;
        remainingSegments = r.remainingSegments;
      }
    } else if (origin && dest) {
      segments = [[origin, dest]];
      if (pos) {
        traveledSegments = [[origin, pos]];
        remainingSegments = [[pos, dest]];
      }
    }
    return { row, key, pos, heading, state, segments, traveledSegments, remainingSegments };
  }).filter((i) => i.pos);
}

// Route colour is the marker's colour (STATUS_COLORS by state), traveled and
// remaining alike. Traveled is solid; the remaining "way to go" is dashed and
// marches toward the destination via the shared .route-future animation.
// Arrived rows have no remaining part: the whole route is solid.

/**
 * @param {object[]} items       buildModeItems output for one mode
 * @param {string}   mode        'air' | 'truck'
 * @param {string}   selectedKey modeRowKey of the selected row
 * @param {string}   hoverKey    modeRowKey of the row under the cursor
 */
const ModeShipmentLayer = ({ items = [], mode = 'air', selectedKey = null, hoverKey = null, onSelect, popupRequest = null }) => {
  // Marker refs so the map-level hit rule can open the winner's popup.
  const markerRefs = useRef(new Map());
  useEffect(() => {
    if (popupRequest && markerRefs.current.has(popupRequest.key)) markerRefs.current.get(popupRequest.key).openPopup();
  }, [popupRequest]);
  // One route at a time: the hovered row previews over the selected one.
  const activeKey = hoverKey || selectedKey;
  const active = useMemo(() => {
    const i = activeKey ? items.find((it) => it.key === activeKey) : null;
    return i && i.segments.length ? i : null;
  }, [items, activeKey]);

  const color = active ? (STATUS_COLORS[active.state] || STATUS_COLORS.on_time) : null;

  return (
    <>
      {active && active.traveledSegments.length > 0 && (
        <Polyline
          key={`route-${active.key}-traveled`}
          positions={active.traveledSegments}
          smoothFactor={1.5}
          className="route-past mode-route"
          pathOptions={{ bubblingMouseEvents: false, color, weight: 4, opacity: 1, lineCap: 'round', lineJoin: 'round' }}
        />
      )}
      {active && active.remainingSegments.length > 0 && (
        <Polyline
          key={`route-${active.key}-remaining`}
          positions={active.remainingSegments}
          smoothFactor={1.5}
          className="route-future mode-route"
          pathOptions={{ bubblingMouseEvents: false, color, weight: 4, opacity: 1, lineCap: 'round', lineJoin: 'round', dashArray: '4 10' }}
        />
      )}

      {items.map((item) => (
        <Marker
          key={`${mode}-${item.key}`}
          ref={(m) => { if (m) markerRefs.current.set(item.key, m); else markerRefs.current.delete(item.key); }}
          position={item.pos}
          icon={createModeIcon({
            mode,
            state: item.state,
            heading: item.heading,
            // Size follows the click, not the cursor: hover only draws the route.
            selected: selectedKey === item.key,
          })}
          zIndexOffset={activeKey === item.key || selectedKey === item.key ? 150000 : 60000}
          eventHandlers={{ click: () => onSelect && onSelect(item.row) }}
        >
          <Popup className="mode-popup-wrap">
            <ModePopup row={item.row} mode={mode} />
          </Popup>
        </Marker>
      ))}
    </>
  );
};

export default ModeShipmentLayer;
